import * as React from "react";
import { Flex, Box, Text, IconButton } from "@radix-ui/themes";
import { FileIcon, XIcon, PaperclipIcon, PaperPlaneRightIcon, StopIcon } from "@phosphor-icons/react";
import { STRINGS } from "../../../constants/strings";

interface ChatInputProps {
  inputValue: string;
  attachedFiles: File[];
  isLoading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  handleSend: () => void;
  handleCancelRun: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (index: number) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  attachedFiles,
  isLoading,
  fileInputRef,
  textareaRef,
  handleSend,
  handleCancelRun,
  handleKeyDown,
  handleInputChange,
  handleFileChange,
  removeFile,
}) => {
  const shouldCancellationGoLive = false;

  return (
    <Box className="chat-input-panel">
      {attachedFiles.length > 0 && (
        <Flex gap="2" wrap="wrap" mb="2">
          {attachedFiles.map((f, i) => (
            <Flex key={i} align="center" gap="1" className="chat-attached-file">
              <FileIcon size={12} />
              <Text size="1" color="gray" className="chat-attached-file-name-long">
                {f.name}
              </Text>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                className="chat-icon-button-small"
                onClick={() => removeFile(i)}
                title="Remove file"
                aria-label="Remove file"
              >
                <XIcon size={12} />
              </IconButton>
            </Flex>
          ))}
        </Flex>
      )}

      <Flex align="end" gap="1" className="chat-input-row">
        <input
          type="file"
          multiple
          ref={fileInputRef}
          className="chat-file-input"
          onChange={handleFileChange}
          disabled={isLoading}
          title="Attach files"
          aria-label="Attach files"
          style={{ display: 'none' }}
        />
        <IconButton
          size="2"
          variant="ghost"
          color="gray"
          onClick={() => fileInputRef.current?.click()}
          className="chat-icon-button-shrink-0"
          disabled={isLoading}
          title="Attach files"
          aria-label="Attach files"
        >
          <PaperclipIcon size={20} />
        </IconButton>

        <textarea
          ref={textareaRef}
          placeholder={STRINGS.CHAT.INPUT_PLACEHOLDER}
          title={STRINGS.CHAT.INPUT_PLACEHOLDER}
          aria-label={STRINGS.CHAT.INPUT_PLACEHOLDER}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="chat-textarea"
        />

        {(isLoading && shouldCancellationGoLive) ? (
          <IconButton
            size="2"
            variant="soft"
            color="red"
            onClick={handleCancelRun}
            className="chat-icon-button-shrink-0"
            title="Stop generating"
            aria-label="Stop generating"
          >
            <StopIcon size={18} weight="fill" />
          </IconButton>
        ) : (
          <IconButton
            size="2"
            variant="solid"
            onClick={handleSend}
            disabled={!inputValue.trim() && attachedFiles.length === 0}
            className="chat-icon-button-shrink-0"
            title="Send message"
            aria-label="Send message"
          >
            <PaperPlaneRightIcon size={18} weight="fill" />
          </IconButton>
        )}
      </Flex>
    </Box>
  );
};
